import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Transaction } from '../types/transaction';

interface TransactionsState {
  items: Transaction[];
  loading: boolean;
  error: string | null;
  uploadProgress: number;
  categorizationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: TransactionsState = {
  items: [],
  loading: false,
  error: null,
  uploadProgress: 0,
  categorizationStatus: 'idle'
};

// Async thunk for saving transactions
export const saveTransactions = createAsyncThunk(
  'transactions/save',
  async (transactions: Transaction[], { dispatch, rejectWithValue }) => {
    try {
      // Input validation
      if (!Array.isArray(transactions) || transactions.length === 0) {
        throw new Error('No transactions provided');
      }

      const { userId } = transactions[0];
      if (!userId) {
        throw new Error('No user ID provided');
      }

      // Validate all transactions have the same userId
      const invalidTransaction = transactions.find(t => t.userId !== userId);
      if (invalidTransaction) {
        throw new Error('All transactions must belong to the same user');
      }

      // Create a document reference with the user's ID
      const userTransactionsRef = doc(db, 'transactions', userId);

      // Check if document already exists
      const existingDoc = await getDoc(userTransactionsRef);
      let existingTransactions: Transaction[] = [];
      
      if (existingDoc.exists()) {
        existingTransactions = existingDoc.data().transactions || [];
        console.log(`Found ${existingTransactions.length} existing transactions`);
      }

      // Combine existing and new transactions
      const allTransactions = [...existingTransactions, ...transactions];
      console.log(`Total transactions: ${allTransactions.length}`);

      // Add metadata and save all transactions in one document
      const userTransactionsDoc = {
        userId,
        lastUpdated: new Date().toISOString(),
        transactionCount: allTransactions.length,
        transactions: allTransactions
      };

      // Save everything in one document
      await setDoc(userTransactionsRef, userTransactionsDoc);
      
      dispatch(setUploadProgress(100));
      return allTransactions;

    } catch (error) {
      console.error('Error saving transactions:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save transactions');
    }
  }
);

// Async thunk for fetching user's transactions
export const fetchTransactions = createAsyncThunk(
  'transactions/fetch',
  async (userId: string, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error('No user ID provided');
      }

      const docRef = doc(db, 'transactions', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data.transactions as Transaction[];
      }
      return [];

    } catch (error) {
      console.error('Error fetching transactions:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch transactions');
    }
  }
);

// Async thunk for clearing user's transactions
export const clearUserTransactions = createAsyncThunk(
  'transactions/clear',
  async (userId: string, { dispatch, rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error('No user ID provided');
      }

      console.log('Clearing transactions for user:', userId); // Debug log

      // Delete the user's transactions document
      const docRef = doc(db, 'transactions', userId);
      
      // Check if document exists first
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        console.log('No transactions found to delete'); // Debug log
        dispatch(clearTransactions());
        return true;
      }

      console.log('Found transactions document, deleting...'); // Debug log
      // await deleteDoc(docRef);
      console.log('Successfully deleted transactions document'); // Debug log
      
      // Clear the local state
      dispatch(clearTransactions());
      
      return true;
    } catch (error) {
      console.error('Error in clearUserTransactions:', error); // Debug log
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to clear transactions');
    }
  }
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setTransactions(state, action: PayloadAction<Transaction[]>) {
      state.items = action.payload;
      state.loading = false;
      state.error = null;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    setUploadProgress(state, action: PayloadAction<number>) {
      state.uploadProgress = action.payload;
    },
    setCategorizationStatus(state, action: PayloadAction<'idle' | 'loading' | 'succeeded' | 'failed'>) {
      state.categorizationStatus = action.payload;
    },
    clearTransactions(state) {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.uploadProgress = 0;
      state.categorizationStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      // Save transactions
      .addCase(saveTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(saveTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Clear transactions
      .addCase(clearUserTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(clearUserTransactions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(clearUserTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to clear transactions';
      });
  },
});

export const { 
  setTransactions, 
  setError, 
  setUploadProgress, 
  clearTransactions,
  setCategorizationStatus 
} = transactionsSlice.actions;
export default transactionsSlice.reducer;